import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  delHealthData,
  normalizeDataMeasures,
  normalizeDataMeasuresDate,
  normalizeDataMeasuresGrouped,
  normalizeDataRelatives,
  normalizeDataUser,
  normalizeDataUserAction,
  normalizeHistoricServiceState,
  normalizeNotesAllTypes,
  normalizePatientAgenda,
  normalizePatientAgendaEvents,
  updateHealthData,
  updateMeasureNotes,
  updateNotes,
  updateRelatives,
} from './operations';
import {
  delPatientNotificationAsync,
  delPatientRulesAsync,
  delRelationOfUserAsync,
  getHistoricServiceStateAsync,
  getHistoricUserDevicesAsync,
  getPatientActionsAsync,
  getPatientActiveAgendaAsync,
  getPatientFinishedAgendaAsync,
  getPatientAgendaEventsAsync,
  getPatientAsync,
  getPatientHealthDataByPathAsync,
  getPatientHealthInsuranceAsync,
  getPatientHealthServiceAsync,
  getPatientInfoDataByPathAsync,
  getPatientLastActionsAsync,
  getPatientLastLocationAsync,
  getPatientMeasuresAsync,
  getPatientMeasuresByTypeAsync,
  getPatientEffectiveMeasureRulesAsync,
  getPatientMeasuresDateAsync,
  getPatientNotesAllTypesAsync,
  getPatientNotesAsync,
  getPatientNotificationsAsync,
  getPatientPersonalDataAsync,
  putPatientPersonalDataAsync,
  getPatientRulesAsync,
  getPatientServiceStateOptionsAsync,
  getPatientServiceTypeOptionsAsync,
  getPatientServiceExpirationAsync,
  getRelationsOfUserAsync,
  getServiceConfigurationAsync,
  getServiceContactWayAsync,
  getServicesStateAsync,
  getTermsAndConditionsAgreementAsync,
  getUserHistoricAsync, getUserUnreadNotificationsCountAsync,
  getVideoCallCloseAsync,
  getVideoCallOpenAsync, postPatientActionsSearchAsync,
  postPatientRulesAsync,
  postReportAsync,
  postServiceConfigurationAsync,
  postServicesStateAsync,
  putDeviceUserAsync,
  putPatientAsync,
  putPatientHealthInsuranceAsync,
  putPatientRulesAsync,
  putRelationshipsPatientAsync,
  putServiceConfigurationAsync,
  putServicesStateAsync,
  putUnlinkDeviceUserAsync
} from "./asyncActions";
import { postPatientsAsync } from 'features/patients/patientsSlice';
import { delByPathIdDevicesAsync } from 'features/devices/devicesSlice';
import { listTransformation } from 'features/groupDetail/operators';
import { dataModeEnum } from '../../const';
import { ERROR_NOT_FOUND } from 'services/api/errorCodes';

import { UserDetailInstancesState } from './interface';
import { DEFAULT_CTX, getCtxFromAction, getInstance, makeInstance } from "./instanceHelpers";
import { AxiosResponse } from 'axios';

// 👇 tipa correctamente el initialState
const initialState: UserDetailInstancesState = {
  instances: {
    [DEFAULT_CTX]: makeInstance()
  }
};

const withCtx = (state: UserDetailInstancesState, action: PayloadAction<AxiosResponse<any> | undefined, string, { arg: { id: string; path: string; contextKey?: string | undefined; }; requestId: string; requestStatus: "fulfilled"; }, never>) => getInstance(state, getCtxFromAction(action));

export const userDetailSlice = createSlice({
  name: 'userDetail',
  initialState,
  reducers: {
    updatePersonalData: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.userData.data = payload;
    },

    updatePatienteNotes: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.notes.data = updateNotes(payload);
    },

    updatePatienteMeasureNotes: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.measures.data = updateMeasureNotes(payload, instance.measures.data);
    },

    setResolution: (state, { payload }: PayloadAction<{ value: any; contextKey?: string } | any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.resolutionSelected = payload;
      instance.openModalResolution = !!payload;
    },

    setOpenModalMeasureFile: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.resolutionSelected = payload;
      instance.openModalMeasureFile = !!payload;
    },

    // Cierre del modal de ficheros de medida. Hace falta una acción propia
    // porque setOpenModalMeasureFile deriva la apertura de !!payload: un
    // { contextKey } para cerrar en su instancia se evaluaría como true.
    resetMeasureFile: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.resolutionSelected = null;
      instance.openModalMeasureFile = false;
    },

    resetResolutions: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.resolutionSelected = null;
      instance.openModalResolution = false;
    },

    setUpdateHealthData: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.healthData.data.pathologies = updateHealthData(payload, instance.healthData.data);
    },

    setDelHealthData: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.healthData.data.pathologies = delHealthData(payload, instance.healthData.data);
    },

    setEditHealthData: (state, { payload }: PayloadAction<{ value: any; contextKey?: string }>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.editHealthData = payload.value;
    },

    resetNewPatient: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.newPatient = null;
    },

    resetAddPatient: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.addPatient = null;
    },

    resetUpdatePatient: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.updatePatient = null;
    },

    resetNewServiceConfiguration: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.serviceConfiguration.newServiceConfiguration = null;
    },

    setClearRulesPatientDeleted: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.rulesDeletedState = null;
    },

    setClearRulesPatientCreated: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.rulesCreatedState = null;
    },

    setRulesPatientSelected: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.ruleSelected = payload;
    },

    setOpenRulesPatientSelected: (
      state,
      { payload }: PayloadAction<{ value: boolean; contextKey?: string } | boolean>,
    ) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.openRuleSelected = value;
    },

    setTabSelected: (state, { payload }: PayloadAction<{ value: any; contextKey?: string } | any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.tabSelected = payload.value ?? payload;
    },

    setUserCreatedId: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.userCreatedId = payload;
    },

    resetUserCreatedId: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.userCreatedId = null;
    },

    resetPatientData: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);

      instance.userData.data = null;
      instance.userHistoric.data = null;
      instance.personalData.data = null;
      instance.healthData.data = {
        pathologies: null,
        interventions: null,
        allergies: null,
        medications: null,
        situations: null,
        vaccines: null,
      };
      instance.personalInfo.data = {
        environmentpets: null,
        environmentservices: null,
        social: null,
        personalcontacts: null,
        generalcontacts: null,
      };
      instance.healthInsurance.data = null;
      instance.measures.data = [];
      instance.measuresByDate.data = [];
      instance.measuresByType.data = {};
      instance.measuresByType.requests = {};
      instance.effectiveMeasureRules.data = [];
      instance.effectiveMeasureRules.status = null;
      instance.effectiveMeasureRules.patientId = null;
      instance.relatives.data = [];
      instance.actions.data = [];
      instance.notifications.data = [];
      instance.termsAndConditionsAgreement.data = [];
      instance.historicServiceState.data = [];
      instance.notes.data = [];
      instance.activeAgenda.data = [];
      instance.videoCall.uri = null;
      instance.report.data = null;
      instance.resolutionSelected = null;
      instance.agendaSelected = null;
      instance.rules.data = [];
      instance.openModalResolution = false;
      instance.openModalMeasureFile = false;
      instance.newPatient = null;
      instance.addPatient = null;
      instance.listServicesOptionsState.data = [];
      // La ficha siempre abre en Resumen. Para entrar en otra pestaña se navega con
      // `history.push(path, { tab })`: UserDetail lo aplica después de este reset.
      instance.tabSelected = 0;
    },

    setOpenModalNotificationShow: (
      state,
      { payload }: PayloadAction<boolean | { value: boolean; contextKey?: string }>,
    ) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.openModalNotificationShow = value;
    },

    setOpenModalNotificationSend: (
      state,
      { payload }: PayloadAction<boolean | { value: boolean; contextKey?: string }>,
    ) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.openModalNotificationSend = value;
    },

    setOpenModalNotificationEdit: (
      state,
      { payload }: PayloadAction<boolean | { value: boolean; contextKey?: string }>,
    ) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.openModalNotificationEdit = value;
    },

    setOpenModalAgenda: (state, { payload }: PayloadAction<boolean | { value: boolean; contextKey?: string }>) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.openAgendaModal = value;
    },

    setOpenModalNote: (state, { payload }: PayloadAction<boolean | { value: boolean; contextKey?: string }>) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.openNoteModal = value;
    },

    setHistoricServiceStateSelected: (state, { payload }: PayloadAction<any>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.historicServiceStateSelected = payload;
    },

    setAgendaSelected: (
      state,
      { payload }: PayloadAction<{ value: dataModeEnum; contextKey?: string } | dataModeEnum>,
    ) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.agendaSelected = value;
    },

    resetAgendaSelected: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.activeAgenda.mode = dataModeEnum.new;
      instance.agendaSelected = null;
      instance.openAgendaModal = false;
    },

    setOpenModalAgendaMode: (
      state,
      { payload }: PayloadAction<{ value: dataModeEnum; contextKey?: string } | dataModeEnum>,
    ) => {
      const ctx = typeof payload === 'object' && 'contextKey' in payload ? payload.contextKey! : DEFAULT_CTX;
      const value = typeof payload === 'object' && 'value' in payload ? payload.value : payload;
      const instance = getInstance(state, ctx);
      instance.activeAgenda.mode = value;
    },

    resetPutServiceState: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.putServicesState.data = null;
    },

    resetPostServicesState: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.postServicesState.data = null;
    },

    resetPatientServicesStateOptions: (state, { payload }: PayloadAction<{ contextKey?: string } | undefined>) => {
      const ctx = payload?.contextKey ?? DEFAULT_CTX;
      const instance = getInstance(state, ctx);
      instance.listServicesOptionsState.data = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // getPatientAsync
      .addCase(getPatientAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.userData.status = 'loading';
      })
      .addCase(getPatientAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.userData.status = action.payload?.data?.errorCode === ERROR_NOT_FOUND ? 'notFound' : 'idle';
        instance.userData.data = normalizeDataUser(action.payload);
        instance.measures.data = [];
        instance.notes.data = [];
        instance.activeAgenda.data = [];
        instance.finishedAgenda.data = [];
        instance.actions.data = [];
        instance.notifications.data = [];
        instance.historicServiceState.data = [];
        instance.termsAndConditionsAgreement.data = [];
        instance.relatives.data = [];
        instance.measuresByType.data = {};
        instance.measuresByType.requests = {};
        instance.effectiveMeasureRules.data = [];
        instance.effectiveMeasureRules.status = null;
        instance.effectiveMeasureRules.patientId = null;
        instance.report.data = null;
      })
      .addCase(getPatientAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.userHistoric.status = 'failed';
      })

      // putPatientAsync
      .addCase(putPatientAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.updatePatient = action.payload?.data;
      })

      // getPatientPersonalDataAsync
      .addCase(getPatientPersonalDataAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.personalData.status = 'loading';
      })
      .addCase(getPatientPersonalDataAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.personalData.status = 'idle';
        instance.personalData.data = action.payload?.data?.item;
      })
      .addCase(getPatientPersonalDataAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.personalData.status = 'failed';
      })

      // putPatientPersonalDataAsync
      .addCase(putPatientPersonalDataAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.personalData.status = 'idle';
      })

      // getUserHistoricAsync
      .addCase(getUserHistoricAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.userHistoric.status = 'failed';
      })
      .addCase(getUserHistoricAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.userHistoric.status = 'loading';
      })
      .addCase(getUserHistoricAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.userHistoric.status = 'idle';
        instance.userHistoric.data = action.payload?.data?.item;
      })

      // postPatientsAsync
      .addCase(postPatientsAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.newPatient = action.payload?.data?.success;
        instance.addPatient = action.payload?.data;
        instance.userCreatedId = action.payload?.data?.item?.id;
      })

      .addCase(getPatientHealthServiceAsync.pending, (state, action) => {
        getInstance(state, getCtxFromAction(action as any)).healthService.status = 'loading';
      })
      .addCase(getPatientHealthServiceAsync.rejected, (state, action) => {
        getInstance(state, getCtxFromAction(action as any)).healthService.status = 'failed';
      })
      // getPatientHealthServiceAsync
      .addCase(getPatientHealthServiceAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.healthService.status = 'idle';
        instance.healthService.data = action.payload?.data?.item?.items;
      })

      // getPatientHealthInsuranceAsync
      .addCase(getPatientHealthInsuranceAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.healthInsurance.status = 'idle';
        instance.healthInsurance.data = action.payload?.data?.item;
      })

      // getPatientMeasuresAsync
      .addCase(getPatientMeasuresAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.measures.status = 'loading';
      })
      .addCase(getPatientMeasuresAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.measures.status = 'idle';
        instance.measures.data = action.meta.arg?.grouped
          ? normalizeDataMeasuresGrouped(
              action.payload?.response,
              action.meta.arg,
              action.payload?.measureTypeLookup ?? {},
            )
          : normalizeDataMeasures(
              action.payload?.response,
              action.meta.arg,
              action.payload?.measureTypeLookup ?? {},
            );
      })
      .addCase(getPatientMeasuresAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.measures.status = 'failed';
      })

      // getPatientMeasuresDateAsync
      .addCase(getPatientMeasuresDateAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.measures.status = 'loading';
      })
      .addCase(getPatientMeasuresDateAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.measures.status = 'idle';
        instance.measures.data = action.meta.arg?.grouped
          ? normalizeDataMeasuresGrouped(
              action.payload?.response,
              action.meta.arg,
              action.payload?.measureTypeLookup ?? {},
            )
          : normalizeDataMeasuresDate(
              action.payload?.response,
              action.meta.arg,
              action.payload?.measureTypeLookup ?? {},
              action.payload?.measureGenericTypeLookup ?? {},
            );
      })
      .addCase(getPatientMeasuresDateAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.measures.status = 'failed';
      })

      // getRelationsOfUserAsync
      .addCase(getRelationsOfUserAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.relatives.status = 'loading';
      })
      .addCase(getRelationsOfUserAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.relatives.status = 'idle';
        instance.relatives.data = normalizeDataRelatives(action.payload);
        instance.relatives.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getRelationsOfUserAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.relatives.status = 'failed';
      })

      // delRelationOfUserAsync
      .addCase(delRelationOfUserAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.relatives.data = instance.relatives.data.filter((item: any) => item.id !== action.meta.arg.id);
      })

      // getPatientActionsAsync
      .addCase(getPatientActionsAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'loading';
      })
      .addCase(getPatientActionsAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'failed';
      })
      .addCase(getPatientActionsAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'idle';
        instance.actions.data = normalizeDataUserAction(action.payload, action.meta);
        instance.actions.pagination.total = action.payload?.data?.item?.total;
      })

      // postPatientActionsSearchAsync
      .addCase(postPatientActionsSearchAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'failed';
      })
      .addCase(postPatientActionsSearchAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'loading';
      })
      .addCase(postPatientActionsSearchAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'idle';
        instance.actions.data = normalizeDataUserAction(action.payload, action.meta);
        // total + facetas sólo vienen en la primera página (init==0); en paginación se conservan.
        if (!action.meta?.arg?.init) {
          instance.actions.filterableValues = action.payload?.data?.item?.filterableValues;
          instance.actions.pagination.total = action.payload?.data?.item?.total;
        }
      })

      // getPatientLastActionsAsync
      .addCase(getPatientLastActionsAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'loading';
      })
      .addCase(getPatientLastActionsAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'idle';
        instance.actions.data = normalizeDataUserAction(action.payload, action.meta);
        instance.actions.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getPatientLastActionsAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.actions.status = 'failed';
      })

      // getPatientNotificationsAsync
      .addCase(getPatientNotificationsAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.notifications.status = 'loading';
      })
      .addCase(getPatientNotificationsAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.notifications.status = 'idle';
        instance.notifications.data = action.payload?.data?.item?.items;
        instance.notifications.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getPatientNotificationsAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.notifications.status = 'failed';
      })

      // delPatientNotificationAsync
      .addCase(delPatientNotificationAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.notifications.data = instance.notifications.data.filter((item: any) => item.id !== action.meta.arg.id);
      })

      // getUserUnreadNotificationsCountAsync
      .addCase(getUserUnreadNotificationsCountAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.unreadNotifications.status = 'loading';
      })
      .addCase(getUserUnreadNotificationsCountAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.unreadNotifications.status = 'idle';
        instance.unreadNotifications.data = action.payload?.item;
      })
      .addCase(getUserUnreadNotificationsCountAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.unreadNotifications.status = 'failed';
      })

      // getTermsAndConditionsAgreementAsync
      .addCase(getTermsAndConditionsAgreementAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.termsAndConditionsAgreement.status = 'loading';
      })
      .addCase(getTermsAndConditionsAgreementAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.termsAndConditionsAgreement.status = 'idle';
        instance.termsAndConditionsAgreement.data = action.payload?.data?.item?.items;
        instance.termsAndConditionsAgreement.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getTermsAndConditionsAgreementAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.termsAndConditionsAgreement.status = 'failed';
      })

      // getHistoricServiceStateAsync
      .addCase(getHistoricServiceStateAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.historicServiceState.status = 'loading';
      })
      .addCase(getHistoricServiceStateAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.historicServiceState.status = 'idle';
        instance.historicServiceState.data = normalizeHistoricServiceState(action.payload?.response, action.payload?.serviceStateLookup ?? {});
        instance.historicServiceState.pagination.total = action.payload?.response?.data?.item?.total;
      })
      .addCase(getHistoricServiceStateAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.historicServiceState.status = 'failed';
      })

      // getServicesStateAsync
      .addCase(getServicesStateAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.servicesState.status = 'loading';
      })
      .addCase(getServicesStateAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.servicesState.status = 'idle';
        instance.servicesState.data = action.payload?.data?.item?.items?.sort((a: any, b: any) => {
          if (a.code.toLowerCase() < b.code.toLowerCase()) return -1;
          if (a.code.toLowerCase() > b.code.toLowerCase()) return 1;
          return 0;
        });
      })
      .addCase(getServicesStateAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.servicesState.status = 'failed';
      })

      // getServiceConfigurationAsync
      .addCase(getServiceConfigurationAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'loading';
      })
      .addCase(getServiceConfigurationAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'idle';
        instance.serviceConfiguration.data = action.payload?.data?.item?.items;
      })
      .addCase(getServiceConfigurationAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'failed';
      })

      // postServiceConfigurationAsync
      .addCase(postServiceConfigurationAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'loading';
      })
      .addCase(postServiceConfigurationAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'idle';
        instance.serviceConfiguration.newServiceConfiguration = action.payload?.data?.success;
      })
      .addCase(postServiceConfigurationAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'failed';
      })

      // putServiceConfigurationAsync
      .addCase(putServiceConfigurationAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'loading';
      })
      .addCase(putServiceConfigurationAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'idle';
        instance.serviceConfiguration.newServiceConfiguration = action.payload?.data?.success;
      })
      .addCase(putServiceConfigurationAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceConfiguration.status = 'failed';
      })

      // getServiceContactWayAsync
      .addCase(getServiceContactWayAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceContactWay.status = 'loading';
      })
      .addCase(getServiceContactWayAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceContactWay.status = 'idle';
        instance.serviceContactWay.data = action.payload?.data?.item?.items;
      })
      .addCase(getServiceContactWayAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceContactWay.status = 'failed';
      })

      // getPatientLastLocationAsync
      .addCase(getPatientLastLocationAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.userData.lastLocation = action.payload?.data?.item;
      })

      // getPatientActiveAgendaAsync
      .addCase(getPatientActiveAgendaAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.activeAgenda.status = 'loading';
      })
      .addCase(getPatientActiveAgendaAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.activeAgenda.status = 'idle';
        instance.activeAgenda.data = normalizePatientAgenda(action.payload);
        //instance.agenda.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getPatientActiveAgendaAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.activeAgenda.status = 'failed';
      })

      // getPatientFinishedAgendaAsync
      .addCase(getPatientFinishedAgendaAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.finishedAgenda.status = 'loading';
      })
      .addCase(getPatientFinishedAgendaAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.finishedAgenda.status = 'idle';
        instance.finishedAgenda.data = normalizePatientAgenda(action.payload);
        //instance.agenda.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getPatientFinishedAgendaAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.finishedAgenda.status = 'failed';
      })

      // getPatientAgendaEventsAsync
      .addCase(getPatientAgendaEventsAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        if (instance.agendaSelected === null) {
          instance.agendaSelected = {};
        }

        instance.agendaSelected.events = normalizePatientAgendaEvents(action.payload);
      })

      // getPatientNotesAsync
      .addCase(getPatientNotesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.notes.status = 'loading';
      })
      .addCase(getPatientNotesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.notes.status = 'idle';
        instance.notes.data = action.payload?.data?.item?.items;
        instance.notes.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getPatientNotesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.notes.status = 'failed';
      })

      // getPatientNotesAllTypesAsync
      .addCase(getPatientNotesAllTypesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.notes.status = 'loading';
      })
      .addCase(getPatientNotesAllTypesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.notes.status = 'idle';
        instance.notesAllTypes.data = normalizeNotesAllTypes(action.payload, action.payload.resolutionLookup ?? {});
        instance.notesAllTypes.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getPatientNotesAllTypesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.notes.status = 'failed';
      })

      // getPatientMeasuresByTypeAsync — bookkeeping is per generic-type code:
      // several types load concurrently and each response only wins if it is
      // still the current request for its code (stale/aborted ones are ignored,
      // so a slow response can't overwrite a newer range).
      .addCase(getPatientMeasuresByTypeAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        const { code, rangeKey } = action.meta.arg;
        instance.measuresByType.status = 'loading';
        instance.measuresByType.requests[code] = { status: 'loading', rangeKey, requestId: action.meta.requestId };
      })
      .addCase(getPatientMeasuresByTypeAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        const { code, rangeKey } = action.meta.arg;
        const request = instance.measuresByType.requests[code];
        if (request && request.requestId !== action.meta.requestId) return; // superseded
        instance.measuresByType.status = 'idle';
        instance.measuresByType.requests[code] = { status: 'idle', rangeKey, requestId: action.meta.requestId };
        instance.measuresByType.data = {
          ...instance.measuresByType.data,
          [code]: action.payload.data?.item?.items,
        };
      })
      .addCase(getPatientMeasuresByTypeAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        const { code, rangeKey } = action.meta.arg;
        const request = instance.measuresByType.requests[code];
        if (request && request.requestId !== action.meta.requestId) return; // superseded
        if (action.meta.aborted) {
          // Dropped on purpose (unmount / range change): forget it so the next
          // mount with the same range fetches again instead of seeing a failure.
          delete instance.measuresByType.requests[code];
          return;
        }
        instance.measuresByType.status = 'failed';
        instance.measuresByType.requests[code] = { status: 'failed', rangeKey, requestId: action.meta.requestId };
      })

      // getPatientEffectiveMeasureRulesAsync
      .addCase(getPatientEffectiveMeasureRulesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.effectiveMeasureRules.status = 'loading';
        instance.effectiveMeasureRules.patientId = (action.meta.arg as any)?.patientId ?? null;
      })
      .addCase(getPatientEffectiveMeasureRulesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.effectiveMeasureRules.status = 'idle';
        instance.effectiveMeasureRules.patientId = (action.meta.arg as any)?.patientId ?? null;
        instance.effectiveMeasureRules.data = action.payload?.data?.items ?? [];
      })
      .addCase(getPatientEffectiveMeasureRulesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.effectiveMeasureRules.status = 'failed';
      })

      // getVideoCallOpenAsync
      .addCase(getVideoCallOpenAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.videoCall.uri = action.payload.data?.item?.uri;
      })

      // getVideoCallCloseAsync
      .addCase(getVideoCallCloseAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.videoCall.uri = '';
      })

      // postReportAsync
      .addCase(postReportAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.report.status = 'loading';
      })
      .addCase(postReportAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.report.status = 'idle';
        instance.report.data = action.payload?.data?.item;
      })
      .addCase(postReportAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.report.status = 'failed';
      })

      // putPatientHealthInsuranceAsync
      .addCase(putPatientHealthInsuranceAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.healthInsurance.data = action.meta.arg.values;
      })

      // putRelationshipsPatientAsync
      .addCase(putRelationshipsPatientAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.relatives.data = updateRelatives(instance.relatives.data, action.meta.arg);
      })

      // getPatientRulesAsync
      .addCase(getPatientRulesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'loading';
      })
      .addCase(getPatientRulesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'idle';
        instance.rules.data = listTransformation(
          action.payload?.data?.items,
          action.meta.arg.optionMeasureUnits,
          action.meta.arg.optionGenericMeasureTypes,
        );
      })
      .addCase(getPatientRulesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'failed';
      })

      // postPatientRulesAsync
      .addCase(postPatientRulesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'loading';
      })
      .addCase(postPatientRulesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'idle';
        instance.rulesCreatedState = action.payload?.data?.success;
      })
      .addCase(postPatientRulesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'failed';
      })

      // putPatientRulesAsync
      .addCase(putPatientRulesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'loading';
      })
      .addCase(putPatientRulesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'idle';
        instance.rulesCreatedState = action.payload?.data?.success;
      })
      .addCase(putPatientRulesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'failed';
      })

      // putDeviceUserAsync
      .addCase(putDeviceUserAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.putLinkDeviceUser.status = 'loading';
      })
      .addCase(putDeviceUserAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.putLinkDeviceUser.status = 'idle';
      })
      .addCase(putDeviceUserAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.putLinkDeviceUser.status = 'failed';
      })

      // putUnlinkDeviceUserAsync
      .addCase(putUnlinkDeviceUserAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.putUnlinkDeviceUser.status = 'loading';
      })
      .addCase(putUnlinkDeviceUserAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.putUnlinkDeviceUser.status = 'idle';
      })
      .addCase(putUnlinkDeviceUserAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.putUnlinkDeviceUser.status = 'failed';
      })

      // delByPathIdDevicesAsync (delete a device from the user's linked devices list)
      .addCase(delByPathIdDevicesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        if (instance.userData.data?.devices) {
          instance.userData.data.devices = instance.userData.data.devices.filter(
            (device: any) => device.id !== action.meta.arg.id,
          );
        }
      })

      // delPatientRulesAsync
      .addCase(delPatientRulesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'loading';
      })
      .addCase(delPatientRulesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'idle';
        instance.rulesDeletedState = action.payload?.data?.success;
      })
      .addCase(delPatientRulesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.rules.status = 'failed';
      })

      .addCase(getPatientHealthDataByPathAsync.pending, (state, action) => {
        getInstance(state, getCtxFromAction(action as any)).healthData.loading[action.meta.arg.path] = true;
      })
      .addCase(getPatientHealthDataByPathAsync.rejected, (state, action) => {
        const instance = getInstance(state, getCtxFromAction(action as any));
        instance.healthData.loading[action.meta.arg.path] = false;
        instance.healthData.status = 'failed';
      })
      // getPatientHealthDataByPathAsync
      .addCase(getPatientHealthDataByPathAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.healthData.status = 'idle';
        instance.healthData.loading[action.meta.arg.path] = false;
        instance.healthData.data = {
          ...instance.healthData.data,
          [action.meta.arg.path]: action.payload?.data?.item?.items,
        };
      })

      .addCase(getPatientInfoDataByPathAsync.pending, (state, action) => {
        getInstance(state, getCtxFromAction(action as any)).personalInfo.loading[action.meta.arg.path.split('-').join('')] = true;
      })
      .addCase(getPatientInfoDataByPathAsync.rejected, (state, action) => {
        const instance = getInstance(state, getCtxFromAction(action as any));
        instance.personalInfo.loading[action.meta.arg.path.split('-').join('')] = false;
        instance.personalInfo.status = 'failed';
      })
      // getPatientInfoDataByPathAsync
      .addCase(getPatientInfoDataByPathAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.personalInfo.status = 'idle';
        instance.personalInfo.loading[action.meta.arg.path.split('-').join('')] = false;
        instance.personalInfo.data = {
          ...instance.personalInfo.data,
          [action.meta.arg.path.split('-').join('')]: action.payload?.data?.item?.items,
        };
      })

      // getHistoricUserDevicesAsync
      .addCase(getHistoricUserDevicesAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.historicDevices.status = 'loading';
      })
      .addCase(getHistoricUserDevicesAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.historicDevices.status = 'idle';
        instance.historicDevices.data = action.payload?.data?.item?.items;
        instance.historicDevices.pagination.total = action.payload?.data?.item?.total;
      })
      .addCase(getHistoricUserDevicesAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.historicDevices.status = 'failed';
      })

      // putServicesStateAsync
      .addCase(putServicesStateAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.putServicesState.status = 'loading';
      })
      .addCase(putServicesStateAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.putServicesState.status = 'idle';
        const data = action.payload?.data;
        if (data && data.errorCode === 2015 && typeof data.message === 'string') {
          data.list = data.message.replace(/The following fields are missing: /g, '').split(', ');
        }
        instance.putServicesState.data = data;
      })
      .addCase(putServicesStateAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.putServicesState.status = 'failed';
      })

      // postServicesStateAsync
      .addCase(postServicesStateAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.postServicesState.status = 'loading';
      })
      .addCase(postServicesStateAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.postServicesState.status = 'idle';
        const data = action.payload?.data;
        if (data && data.errorCode === 2015 && typeof data.message === 'string') {
          data.list = data.message.replace(/The following fields are missing: /g, '').split(', ');
        }
        instance.postServicesState.data = data;
      })
      .addCase(postServicesStateAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.postServicesState.status = 'failed';
      })

      // getPatientServiceStateOptionsAsync
      .addCase(getPatientServiceStateOptionsAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.listServicesOptionsState.status = 'loading';
      })
      .addCase(getPatientServiceStateOptionsAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.listServicesOptionsState.status = 'idle';
        instance.listServicesOptionsState.data = action.payload.data.items;
      })
      .addCase(getPatientServiceStateOptionsAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.listServicesOptionsState.status = 'failed';
      })

      // getPatientServiceTypeOptionsAsync
      .addCase(getPatientServiceTypeOptionsAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.listServicesTypeOptions.status = 'loading';
      })
      .addCase(getPatientServiceTypeOptionsAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.listServicesTypeOptions.status = 'idle';
        instance.listServicesTypeOptions.data = action.payload?.data?.items ?? [];
      })
      .addCase(getPatientServiceTypeOptionsAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.listServicesTypeOptions.status = 'failed';
      })

      // getPatientServiceExpirationAsync
      .addCase(getPatientServiceExpirationAsync.pending, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceExpiration.status = 'loading';
      })
      .addCase(getPatientServiceExpirationAsync.fulfilled, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceExpiration.status = 'idle';
        // item viene a null cuando el servicio no se ha activado nunca: es un estado válido.
        instance.serviceExpiration.data = action.payload?.data?.item ?? null;
      })
      .addCase(getPatientServiceExpirationAsync.rejected, (state, action) => {
        const instance = withCtx(state, action);
        instance.serviceExpiration.status = 'failed';
      });
  },
});

export const {
  updatePersonalData,
  updatePatienteNotes,
  updatePatienteMeasureNotes,
  setResolution,
  resetResolutions,
  setOpenModalMeasureFile,
  resetMeasureFile,
  setUpdateHealthData,
  setDelHealthData,
  setEditHealthData,
  resetPatientData,
  resetNewPatient,
  resetAddPatient,
  resetUpdatePatient,
  resetNewServiceConfiguration,
  setClearRulesPatientCreated,
  setClearRulesPatientDeleted,
  setRulesPatientSelected,
  setOpenRulesPatientSelected,
  setTabSelected,
  resetUserCreatedId,
  setOpenModalNotificationShow,
  setOpenModalNotificationSend,
  setOpenModalNotificationEdit,
  setOpenModalNote,
  setHistoricServiceStateSelected,
  setOpenModalAgenda,
  setAgendaSelected,
  resetAgendaSelected,
  setOpenModalAgendaMode,
  resetPutServiceState,
  resetPostServicesState,
} = userDetailSlice.actions;

export default userDetailSlice.reducer;
